/**
 * The app: sign-in (Gate), then the shell around the routed screen. Everything here is built from the registries:
 * src/shared/screen-meta.ts (+ src/screens.ts) for Operate, src/learn/nav.ts for Learn.
 *
 * - The error boundary sits inside the shell, around the routed screen, so a broken screen leaves the nav working.
 * - Each screen's code downloads on first open; the boundary catches a failed download too.
 * - /__ui, the component board, exists in development only: the production build compiles it out (scripts/check-dist.ts).
 */
import { lazy, Suspense, useMemo, useState, type ComponentType, type LazyExoticComponent } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router'
import { isPreviewHost } from '../learn/checks'
import { learnNav, learnRoutes } from '../learn/nav'
import { screens, type Screen } from '../screens'
import { canSeeScreen, isBuilder } from '../shared/access'
import { AppShell, CommandPalette, EmptyState, Loading, Notice, ScreenMeta, type NavItem } from '../ui'
import { useAccess } from './access'
import { buildCommands } from './commands'
import { Gate } from './Gate'
import { RouteBoundary } from './ScreenBoundary'
import { useSession } from './session'
import { documentTitle, screenForPath, useDocumentTitle } from './title'

// Development only. In a production build `import.meta.env.DEV` is false, so this import and its file disappear.
const Board: LazyExoticComponent<ComponentType> | null = import.meta.env.DEV ? lazy(() => import('../board/Board')) : null

/** One screen: its registry title and description for <Screen>, and its code loading on first open. */
function ScreenRoute({ screen }: { screen: Screen }) {
  const meta = useMemo(() => ({ title: screen.title, description: screen.description }), [screen])
  return (
    <ScreenMeta.Provider value={meta}>
      <Suspense fallback={<Loading shape="page" />}>
        <screen.component />
      </Suspense>
    </ScreenMeta.Provider>
  )
}

function Lazy({ component: C }: { component: LazyExoticComponent<ComponentType> }) {
  return (
    <Suspense fallback={<Loading shape="block" />}>
      <C />
    </Suspense>
  )
}

const toNav = (s: Screen): NavItem => ({ to: s.path, label: s.title, icon: s.icon, group: s.group, tab: s.tab, draft: s.draft })

function Frame() {
  const access = useAccess()
  const { logout } = useSession()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const builder = isBuilder(access)
  // The demo: nobody signed in, so there's no one to sign out.
  const signOut = access.demo ? undefined : logout
  const visible = useMemo(() => screens.filter((s) => canSeeScreen(access, s.path)), [access])
  const learn = builder ? learnNav : null
  const inLearn = pathname === '/learn' || pathname.startsWith('/learn/')
  const commands = useMemo(
    () => buildCommands({ screens: visible, learn, navigate: (to) => void navigate(to), signOut, board: Board !== null && builder }),
    [visible, learn, navigate, signOut, builder],
  )
  // Outside the boundary, so the tab keeps the screen's name even while the screen shows an error.
  useDocumentTitle(documentTitle(inLearn ? 'Learn' : screenForPath(visible, pathname)?.title, access.appName))

  if (Board && builder && pathname.startsWith('/__ui')) return <Lazy component={Board} />
  if (visible.length === 0 && !builder) {
    return <Notice title="Nothing here yet" body="No screens have been shared with you in this app yet." />
  }
  const home = visible[0]?.path ?? '/learn'
  return (
    <>
      <AppShell
        appName={access.appName}
        section={inLearn ? 'learn' : 'operate'}
        operate={visible.map(toNav)}
        learn={learn}
        bare={!builder && visible.length === 1}
        preview={import.meta.env.DEV || isPreviewHost(location.hostname)}
        demo={access.demo}
        user={access.user.email ?? access.user.name ?? 'Signed in'}
        onSignOut={signOut}
        onSearch={() => setPaletteOpen(true)}
      >
        <RouteBoundary>
          <Routes>
            <Route path="/" element={<Navigate to={home} replace />} />
            {visible.map((s) => (
              <Route key={s.path} path={s.path} element={<ScreenRoute screen={s} />} />
            ))}
            {builder &&
              learnRoutes.map((r) => <Route key={r.path} path={r.path} element={<Lazy component={r.component} />} />)}
            <Route path="*" element={<EmptyState title="Nothing at this address" body="Pick a screen from the menu." />} />
          </Routes>
        </RouteBoundary>
      </AppShell>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} commands={commands} />
    </>
  )
}

export function App() {
  return (
    <BrowserRouter>
      <Gate>
        <Frame />
      </Gate>
    </BrowserRouter>
  )
}
